import { FieldSet, FormattedString, InputWizardPanelComponent, Select, TextInput } from "o4a-react";
import * as React from "react";
import * as Messages from "../../codegen/Messages";
import { ExadataDbSystemShapeSelect } from "../../components/DbSystemShapeSelect/ExadataDbSystemShapeSelect";
import { ExaDbVersionSelect } from "../../components/DbVersionSelect/ExaDbVersionSelect";
import { GridInfraVersionSelect } from "../../components/GridInfraVersionSelect/GridInfraVersionSelect";
import { LicenseTypeSelect } from "../../components/LicenseTypeSelect/LicenseTypeSelect";
import { StorageConfigInput } from "../../components/StorageConfigInput/StorageConfigInput";
import { VmClusterConfigInput } from "../../components/VmClusterConfigInput/VmClusterConfigInput";
import { VmClusterSelect } from "../../components/VmClusterSelect/VmClusterSelect";
import { exaPluggableDatabaseUrl } from "../../constants/docConstants";
import {
  CharacterSets,
  defaultCharacterSet,
  defaultNationalCharacterSet,
  ExadataCreateFlowType,
  NationalCharacterSets,
} from "../../helpers/resourceHelper";
import {
  alphaNumericRegex,
  alphaNumericUnderscoreRegex,
  pdbNameMaxLength,
  startWithLetterRegex,
} from "../../helpers/validationHelper";

export const GROUP_CONFIG = "config";

export enum Fields {
  ExadataSystemModel = "cloudExadataInfrastructureDetails.shape",
  OracleGridInfrastructureVersion = "cloudVmClusterDetails.giVersion",
  LicenseType = "cloudVmClusterDetails.licenseModel",
  VmClusterConfiguration = "cloudVmClusterDetails.cpuCoreCount",
  StorageConfiguration = "cloudVmClusterDetails.storageConfiguration",
  VMCluster = "cloudVmClusterDetails.id",
  DatabaseVersion = "databaseDetails.dbVersion",
  DatabaseName = "databaseDetails.dbName",
  PdbName = "databaseDetails.pdbName",
  // This field is not used in any submit function currently
  BackupName = "backupName",
  CharacterSet = "databaseDetails.characterSet",
  NationalCharacterSet = "databaseDetails.ncharacterSet",
}

const defaultShapeName = "Exadata.X8M";
interface VmClusterConfigInputValue {
  value: number;
}

export interface BackupInfo {
  id: string,
  name: string,
  startDate: Date | undefined,
}
export interface ConfigTabContentProps {
  createFlowType?: ExadataCreateFlowType;
  inputWizardPanelRef: InputWizardPanelComponent;
  subscriptionId: string;
  resourceGroupName: string;
  location: string;
  vmClusterId?: string;
  backup?: BackupInfo;
  disableUIControls?: Fields[] | undefined;
}

const characterSetOptions = CharacterSets.map(set => ({
  id: set,
  text: set,
}));

const natioanlCharacterSetOptions = NationalCharacterSets.map(set => ({
  id: set,
  text: set,
}));

export const ConfigTabContent = (
  {
    createFlowType, inputWizardPanelRef, subscriptionId,
    resourceGroupName, location, vmClusterId, backup, disableUIControls,
  }: ConfigTabContentProps,
): JSX.Element => {
  const [shapeName, setShapeName] = React.useState<string>(defaultShapeName);
  const [dbServers, setDBServers] = React.useState<number>();
  const [
    , setVmClusterConfigInputValue,
  ] = React.useState<VmClusterConfigInputValue>();

  const onError = (): void => inputWizardPanelRef.showError(Messages.createCommon.loadingErrors.general());
  const onMissingDependencies = (): void => inputWizardPanelRef.showErrorDialog(
    Messages.createCommon.dependencyWarnings.basicsIncomplete.message(),
    Messages.createCommon.dependencyWarnings.basicsIncomplete.title(),
  );

  return (
    <>
      {createFlowType === ExadataCreateFlowType.EXADATA_SYSTEM && (
        <>
          <FieldSet
            header={Messages.createExaDb.configurationTab.sectionTitles.infrastructureDetail()}
          >
            <ExadataDbSystemShapeSelect
              fieldName={Fields.ExadataSystemModel}
              label={Messages.labels.exadataSystemModel()}
              required
              defaultValue={[defaultShapeName]}
              subscriptionId={subscriptionId}
              location={location}
              tooltip={Messages.hints.selectAExadataBase()}
              onShapeChange={(sName: string) => {
                setShapeName(sName);
              }}
              onDBServerCountChange={(value: number) => {
                setDBServers(value);
              }}
              onError={onError}
              onMissingDependencies={onMissingDependencies}
            />
          </FieldSet>
          <FieldSet
            header={Messages.createExaDb.configurationTab.sectionTitles.vmCluster()}
          >
            <VmClusterConfigInput
              fieldName={Fields.VmClusterConfiguration}
              label={Messages.labels.vmClusterConfiguration()}
              required
              subscriptionId={subscriptionId}
              location={location}
              dbSystemShape={shapeName}
              dbServers={dbServers}
              isSystemCreate={createFlowType === ExadataCreateFlowType.EXADATA_SYSTEM}
              callback={(value: VmClusterConfigInputValue) => { setVmClusterConfigInputValue(value); }}
            />
            <StorageConfigInput
              label={Messages.labels.storage()}
              fieldName={Fields.StorageConfiguration}
            />
            <GridInfraVersionSelect
              fieldName={Fields.OracleGridInfrastructureVersion}
              label={Messages.labels.oracleGridInfrastructureVersion()}
              required
              tooltip={Messages.hints.selectOracleGridInfrastructureVersion()}
              subscriptionId={subscriptionId}
              location={location}
              shape={shapeName}
              onError={onError}
              onMissingDependencies={onMissingDependencies}
            />
            <LicenseTypeSelect
              fieldName={Fields.LicenseType}
            />
          </FieldSet>
        </>
      )}
      {createFlowType !== ExadataCreateFlowType.EXADATA_SYSTEM && (
        <FieldSet header={Messages.createExaDb.configurationTab.sectionTitles.vmCluster()}>
          <VmClusterSelect
            required
            fieldName={Fields.VMCluster}
            label={Messages.labels.vmCluster()}
            tooltip={Messages.labels.vmCluster()}
            resourceGroupName={resourceGroupName}
            subscriptionId={subscriptionId}
            location={location}
            onError={onError}
            onMissingDependencies={onMissingDependencies}
            defaultValue={vmClusterId ? [vmClusterId] : undefined}
            disabled={disableUIControls?.find(ele => ele === Fields.VMCluster) !== undefined}
          />
        </FieldSet>
      )}
      <FieldSet
        header={Messages.createExaDb.configurationTab.sectionTitles.databaseDetail()}
      >
        {createFlowType !== ExadataCreateFlowType.EXADATA_DATABASE_FROM_BACKUP && (
          <ExaDbVersionSelect
            required
            fieldName={Fields.DatabaseVersion}
            label={Messages.labels.databaseVersion()}
            tooltip={Messages.labels.databaseVersion()}
            subscriptionId={subscriptionId}
            location={location}
            onError={onError}
            onMissingDependencies={onMissingDependencies}
          />
        )}
        {createFlowType === ExadataCreateFlowType.EXADATA_DATABASE_FROM_BACKUP && (
          <TextInput
            fieldName={Fields.BackupName}
            label={Messages.labels.databaseBackupName()}
            disabled={disableUIControls?.find(ele => ele === Fields.BackupName) !== undefined}
            defaultValue={`${backup?.name} (${backup?.startDate})`}
          />
        )}
        <TextInput
          required
          fieldName={Fields.DatabaseName}
          label={Messages.labels.databaseName()}
          tooltip={Messages.labels.databaseName()}
          validator={(value: string) => {
            const errors: string[] = [];
            if (value && !startWithLetterRegex.test(value)) {
              errors.push(Messages.validation.nameStartChar());
            }
            if (value && !alphaNumericRegex.test(value)) {
              errors.push(Messages.validation.valueAlphaNumeric());
            }
            if (value?.length > 8) {
              errors.push(Messages.validation.valueMaxLen("8"));
            }
            return errors.length > 0 ? errors : undefined;
          }}
        />
        {createFlowType !== ExadataCreateFlowType.EXADATA_DATABASE_FROM_BACKUP && (
          <TextInput
            fieldName={Fields.PdbName}
            label={Messages.labels.pdbName()}
            tooltip={
              FormattedString(
                { inputText: Messages.hints.tooltipPluggableDatabase(exaPluggableDatabaseUrl) },
              ) as unknown as string
            }
            validator={(value: string) => {
              const errors: string [] = [];
              if (value && !startWithLetterRegex.test(value)) {
                errors.push(Messages.validation.nameStartChar());
              }
              if (value?.length > pdbNameMaxLength) {
                errors.push(Messages.validation.valueMaxLen(pdbNameMaxLength.toString()));
              }
              if (value && !alphaNumericUnderscoreRegex.test(value)) {
                errors.push(Messages.validation.valueAlphaNumericUnderscore());
              }
              return errors.length > 0 ? errors : undefined;
            }}
          />
        )}
        { createFlowType !== ExadataCreateFlowType.EXADATA_DATABASE_FROM_BACKUP && (
          <>
            <Select
              label={Messages.labels.characterSet()}
              required
              fieldName={Fields.CharacterSet}
              options={characterSetOptions}
              defaultValue={[defaultCharacterSet]}
            />
            <Select
              label={Messages.labels.nationalCharacterSet()}
              required
              fieldName={Fields.NationalCharacterSet}
              options={natioanlCharacterSetOptions}
              defaultValue={[defaultNationalCharacterSet]}
            />
          </>
        )}
      </FieldSet>
    </>
  );
};
